import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';

import {AppState} from "lib/redux/store";
import {AlgoliaCredentials, wineAppApi} from "lib/api/wineAppApi";

const selectors = {
    appId: (state: AppState) => state.algoliaCredentials.appId,
    apiKey: (state: AppState) => state.algoliaCredentials.apiKey,
    productIndex: (state: AppState) => state.algoliaCredentials.productIndex,
    fetchLoading: (state: AppState) => state.algoliaCredentials.requests.fetchCredentials.loading,
    fetchError: (state: AppState) => state.algoliaCredentials.requests.fetchCredentials.error
};

const fetchCredentials = createAsyncThunk<AlgoliaCredentials, void, { state: AppState }>(
    'algolia/fetchCredentials',
    (_: void, thunkAPI) => {
        const state = thunkAPI.getState()
        const error = selectors.fetchError(state);
        if (error) {
            return Promise.reject(error);
        }
        return wineAppApi.getAlgoliaCredentials();
    }
);

interface AlgoliaCredentialsState {
    appId: string | null
    apiKey: string | null
    productIndex: string | null
    requests: {
        fetchCredentials: {
            loading: boolean
            error: string | null
        }
    }
}

const initialState: AlgoliaCredentialsState = {
    appId: null,
    apiKey: null,
    productIndex: null,
    requests: {
        fetchCredentials: {
            loading: false,
            error: null
        }
    }
}

const {actions, reducer} = createSlice({
    name: 'algoliaCredentials',
    initialState: initialState,
    reducers: {
        clear: (state) => {
            state.appId = null;
            state.apiKey = null;
            state.productIndex = null;
        }
    },
    extraReducers: builder => {
        builder.addCase(fetchCredentials.pending, (state: AlgoliaCredentialsState) => {
            state.requests.fetchCredentials.loading = true;
            state.requests.fetchCredentials.error = null;
        });

        builder.addCase(fetchCredentials.fulfilled, (state, action) => {
            state.requests.fetchCredentials.loading = false;
            state.requests.fetchCredentials.error = null;
            state.appId = action.payload.appId;
            state.apiKey = action.payload.apiKey;
            state.productIndex = action.payload.productIndex;
        });

        builder.addCase(fetchCredentials.rejected, (state, action) => {
            state.requests.fetchCredentials.loading = false;
            state.requests.fetchCredentials.error = action.error.message ?? null;
        });
    }
});

const publicActions = {
    clear: actions.clear,
    fetchCredentials
};

export {publicActions as algoliaCredentialsActions};

export {selectors as algoliaCredentialsSelectors};

export {reducer as algoliaCredentialsReducer};
